import React, { useEffect, useMemo, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import {
  Box,
  FormControl,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import Button from '@material-ui/core/Button';
import { colors } from 'theme';
import Dialog from 'components/Dialog';
import { DialogWidth, ExpeditionBuyerConfirmationForm } from 'types';
import DatePicker from 'components/CustomInputs/DatePicker';
import WeightInputField from 'components/CustomInputs/WeightInputField';
import { CancelIcon, ValidateIcon } from 'components/Icons';
import { expeditionBuyerConfirmationSchema } from './ExpeditionBuyerConfirmationCreateForm.yup';

const useStyles = makeStyles(theme => ({
  buyerConfirmationTab: {
    display: 'flex',
    width: '55%',
    justifyContent: 'space-between',
    marginBottom: '36px',
    paddingTop: '28px',
  },
  headerRow: {
    '& th': {
      width: '33%',
      borderWidth: '2px',
      '&:first-child': {
        paddingLeft: '40px',
      },
      '&:last-child': {
        paddingRight: '30px',
      },
    },
  },
  dataRow: {
    '& td': {
      width: '33%',
      borderBottom: 'none',
      '&:first-child': {
        paddingLeft: '40px',
      },
      '&:last-child': {
        paddingRight: '30px',
      },
    },
  },
  smallTablePaper: {
    width: '100%',
    borderLeft: `6px solid ${colors.greenPastel}`,
  },
  headerTitle: {
    marginBottom: '2rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignContent: 'center',
  },
  title: {
    margin: '0',
  },
  buttonBar: {
    display: 'flex',
    justifyContent: 'center',
    '& > *': {
      margin: '0 10px',
    },
  },
  buttonEdit: {
    background: colors.grey900,
    color: 'white',
  },
  buttonLine: {
    display: 'flex',
    marginTop: '25px',
    marginBottom: '15px',
    justifyContent: 'center',
  },
  button: {
    display: 'flex',
    alignItems: 'end',
    padding: '10px',
    margin: '0 10px',
  },
  icon: {
    color: theme.palette.text.primary,
    fontSize: 'medium',
    fontWeight: 600,
    marginLeft: '6px',
  },
  dateSize: {
    width: '90%',
    textAlign: 'right',
  },
  inputSize: {
    width: '60%',
    textAlign: 'right',
  },
}));

export interface ExpeditionBuyerConfirmationFormProps {
  onSubmit: SubmitHandler<ExpeditionBuyerConfirmationForm>;
  formName?: string;
  editMode?: boolean;
  expeditionDate: Date | null;
  resetFormValues: ExpeditionBuyerConfirmationDefaults | null;
  setCurrentTonnageAccepteInTons: (current: number) => void;
  setCurrentTonnageDecoteInTons: (current: number) => void;
  setCurrentConfirmationDate: (current: Date | null) => void;
  setIsAutoTonnageRepris: (current: boolean) => void;
  isAutoTonnageRepris: boolean;
}

interface ExpeditionBuyerConfirmationDefaults {
  confirmationDate: Date | null;
  weight: number | null;
  decote: number | null;
}

const ExpeditionBuyerConfirmationCreateForm: React.FC<ExpeditionBuyerConfirmationFormProps> = ({
  onSubmit,
  formName,
  editMode,
  expeditionDate,
  resetFormValues,
  setCurrentTonnageAccepteInTons,
  setCurrentConfirmationDate,
  setCurrentTonnageDecoteInTons,
  setIsAutoTonnageRepris,
  isAutoTonnageRepris,
}) => {
  const classes = useStyles();
  const { t: pt } = useTranslation('translation', { keyPrefix: 'expeditions.show.buyerConfirmationTab' });

  const schema = useMemo(() => expeditionBuyerConfirmationSchema(pt, expeditionDate), [pt, expeditionDate]);

  const { control, handleSubmit, trigger, reset, watch } = useForm<ExpeditionBuyerConfirmationForm>({
    mode: 'onBlur',
    resolver: yupResolver(schema),
  });

  const watchedWeight = watch('weight');
  const watchedDecote = watch('decote');
  const watchedConfirmationDate = watch('confirmationDate');
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);

  useEffect(() => {
    reset({
      decote: resetFormValues ? resetFormValues.decote : null,
      weight: resetFormValues ? resetFormValues.weight : null,
      confirmationDate: resetFormValues ? resetFormValues.confirmationDate : null,
    });
  }, [resetFormValues, reset]);

  useEffect(() => {
    setCurrentTonnageAccepteInTons(watchedWeight ?? 0); //see ET-2546 for more informations
  }, [watchedWeight]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setCurrentTonnageDecoteInTons(watchedDecote ?? 0); // see ET-2546 for more informations
  }, [watchedDecote]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setCurrentConfirmationDate(watchedConfirmationDate); // see ET-2546 for more informations
  }, [watchedConfirmationDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAutoTonnageRepris = () => {
    setCurrentTonnageAccepteInTons(watchedWeight ?? 0);
    setIsAutoTonnageRepris(true);
    setIsDialogOpen(false);
  };

  const handleDialog = () => {
    if (
      resetFormValues &&
      resetFormValues.weight &&
      resetFormValues.weight > 0 &&
      resetFormValues.weight !== Number(watchedWeight) &&
      Number(watchedWeight) > 0 &&
      !isAutoTonnageRepris
    ) {
      setIsDialogOpen(true);
    }
  };

  return (
    <>
      <Dialog
        closable={false}
        renderButton={() => {}}
        isOpen={isDialogOpen}
        width={DialogWidth.LARGE_FORM}
        renderContent={() => (
          <>
            <Typography variant="h2">{pt('dialog.updateTonnage.title')}</Typography>
            <div className={classes.buttonLine}>
              <Button
                variant="outlined"
                className={classes.button}
                onClick={() => {
                  setIsDialogOpen(false);
                }}
              >
                {pt('dialog.updateTonnage.back')}
                <CancelIcon className={classes.icon} />
              </Button>
              <Button
                variant="outlined"
                className={classes.button}
                onClick={() => {
                  handleAutoTonnageRepris();
                }}
              >
                {pt('dialog.updateTonnage.submit')}
                <ValidateIcon className={classes.icon} />
              </Button>
            </div>
          </>
        )}
      />
      <form id={formName} onSubmit={handleSubmit(onSubmit)} className={classes.buyerConfirmationTab}>
        <Paper square className={classes.smallTablePaper}>
          <Box display="flex" paddingLeft="21px" paddingRight="21px">
            <Table>
              <TableHead>
                <TableRow className={classes.headerRow}>
                  <TableCell align="left">{pt('confirmationDate.title')}</TableCell>
                  <TableCell align="center">{pt('decote')}</TableCell>
                  <TableCell align="right">{pt('receivedWeight')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow className={classes.dataRow}>
                  <TableCell align="left">
                    {resetFormValues?.weight || editMode ? (
                      <FormControl className={classes.dateSize}>
                        <Controller
                          name="confirmationDate"
                          control={control}
                          render={({ field: { ref, onBlur, ...inputProps }, fieldState }) => (
                            <DatePicker
                              id="confirmationDate"
                              inputRef={ref}
                              inputVariant="standard"
                              variant="inline"
                              initialFocusedDate={expeditionDate}
                              disabled={!editMode}
                              error={fieldState.invalid}
                              helperText={fieldState.error?.message}
                              onAccept={() => {
                                trigger('confirmationDate');
                              }}
                              onBlur={() => {
                                trigger('confirmationDate');
                              }}
                              {...inputProps}
                            />
                          )}
                        />
                      </FormControl>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {resetFormValues?.decote || editMode ? (
                      <FormControl className={classes.inputSize}>
                        <Controller
                          name="decote"
                          control={control}
                          render={({ field: { ref, onBlur, ...fieldProps }, fieldState }) => (
                            <WeightInputField
                              id="decote"
                              inputRef={ref}
                              error={fieldState.invalid}
                              helperText={fieldState.error?.message}
                              disabled={!editMode}
                              onBlur={() => {
                                trigger('decote');
                              }}
                              placeholder="0,000 t"
                              {...fieldProps}
                            />
                          )}
                        />
                      </FormControl>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {resetFormValues?.weight || editMode ? (
                      <FormControl className={classes.inputSize}>
                        <Controller
                          name="weight"
                          control={control}
                          render={({ field: { ref, onBlur, ...fieldProps }, fieldState }) => (
                            <WeightInputField
                              id="weight"
                              inputRef={ref}
                              error={fieldState.invalid}
                              helperText={fieldState.error?.message}
                              disabled={!editMode}
                              InputLabelProps={{ required: true }}
                              onBlur={() => {
                                trigger('weight');
                                handleDialog();
                              }}
                              placeholder="0,000 t"
                              {...fieldProps}
                            />
                          )}
                        />
                      </FormControl>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>
        </Paper>
      </form>
    </>
  );
};
export default ExpeditionBuyerConfirmationCreateForm;
